<template>
  <div>
    <!-- Dialog para analisar logs com IA -->
    <v-dialog v-model="internalDialog" max-width="700px" class="modern-dialog">
      <v-card class="modern-dialog-card">
        <v-card-title class="modern-dialog-title">
          <v-icon class="mr-2" color="primary">mdi-brain</v-icon>
          <span class="text-h6 font-weight-bold">Análise de Logs com IA</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog" class="close-btn">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text>
          <div v-if="loadingAnalysis" class="d-flex flex-column align-center justify-center py-5">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
            <p class="mt-4 text-subtitle-1">Analisando logs com IA...</p>
            <p class="text-caption text--secondary">Isso pode levar alguns instantes.</p>
          </div>
          
          <div v-else>
            <v-row>
              <v-col cols="12">
                <div class="d-flex align-center mb-3">
                  <v-chip small class="mr-2">{{ hostName }}</v-chip>
                  <span class="text-caption text--secondary">{{ dataFormatada }}</span>
                  <v-chip small class="ml-2" color="primary">{{ sistema }}</v-chip>
                </div>

                <v-alert
                  :color="error ? 'error' : 'info'"
                  :icon="error ? 'mdi-alert-circle' : 'mdi-information'"
                  border="left"
                  colored-border
                  elevation="2"
                  class="mb-3"
                >
                  {{ error || analysisResult || 'A IA não conseguiu determinar a causa do erro.' }}
                </v-alert>
              </v-col>
            </v-row>

            <div v-if="logContent" class="mt-4">
              <v-card outlined>
                <v-card-title class="py-2 subtitle-1">
                  <v-icon small class="mr-2">mdi-text</v-icon>
                  Trecho do log analisado
                  <v-spacer></v-spacer>
                  <v-btn x-small text @click="showFullLog = !showFullLog">
                    {{ showFullLog ? 'Mostrar menos' : 'Mostrar tudo' }}
                  </v-btn>
                </v-card-title>
                
                <v-card-text class="pa-0">
                  <pre class="log-preview" :class="{'expanded': showFullLog}" v-html="formattedLogContent"></pre>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">
            Fechar
          </v-btn>
          <v-btn 
            color="primary" 
            :disabled="loadingAnalysis || error" 
            @click="copyAnalysisToClipboard"
          >
            <v-icon left>mdi-content-copy</v-icon>
            Copiar análise
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import dashboardService from '@/services/dashboardService'
import { formatarLog } from '@/utils/formatterlog'

export default {
  name: 'DashboardIaAnalyzer',
  
  props: {
    value: {
      type: Boolean,
      default: false
    },
    atualizacaoId: {
      type: Number,
      required: true
    },
    hostId: {
      type: String,
      required: true
    },
    hostName: {
      type: String,
      required: true
    },
    sistema: {
      type: String,
      required: true
    },
    dataFormatada: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      loadingAnalysis: false,
      analysisResult: null,
      logContent: null,
      error: null,
      showFullLog: false
    }
  },

  computed: {
    internalDialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    
    formattedLogContent() {
      if (!this.logContent) return '';
      return formatarLog(this.logContent, false); // false para não mostrar debug
    }
  },

  watch: {
    value: {
      handler(newVal) {
        if (newVal && this.atualizacaoId) {
          this.analisarLog()
        }
      },
      immediate: true
    }
  },

  methods: {
    async analisarLog() {
      if (!this.atualizacaoId) {
        this.error = 'ID da atualização não fornecido'
        return
      }
      
      this.loadingAnalysis = true
      this.error = null
      this.analysisResult = null
      
      try {
        // 1. Obter logs da atualização
        const logsResponse = await dashboardService.getLogAtualizacao(this.atualizacaoId)
        
        // Verificar a estrutura correta da resposta
        let logs = null
        if (logsResponse && logsResponse.controle && logsResponse.controle.logs) {
          logs = logsResponse.controle.logs
        } else if (logsResponse && logsResponse.logs) {
          logs = logsResponse.logs
        }
        
        if (!logs) {
          throw new Error('Não foi possível obter logs para esta atualização')
        }
        
        this.logContent = logs
        
        // 2. Enviar para análise de IA
        const analysisResponse = await dashboardService.analisarLogIA(logs)
        
        if (!analysisResponse || !analysisResponse.analise) {
          throw new Error('A análise não retornou resultados')
        }
        
        this.analysisResult = analysisResponse.analise
      } catch (error) {
        console.error('Erro ao analisar logs:', error)
        this.error = error.message || 'Ocorreu um erro ao analisar os logs'
        this.$emit('show-snackbar', this.error, 'error')
      } finally {
        this.loadingAnalysis = false
      }
    },

    closeDialog() {
      this.internalDialog = false
    },
    
    copyAnalysisToClipboard() {
      if (!this.analysisResult) return
      
      try {
        navigator.clipboard.writeText(this.analysisResult)
        this.$emit('show-snackbar', 'Análise copiada para a área de transferência', 'success')
      } catch (error) {
        this.$emit('show-snackbar', 'Não foi possível copiar a análise', 'error')
      }
    }
  }
}
</script>

<style scoped>
.log-preview {
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 8px;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 200px;
  overflow-y: auto;
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  line-height: 1.4;
  color: #333;
  border: 1px solid rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.log-preview.expanded {
  max-height: 400px;
}

/* Tema escuro para visualização de logs */
.theme-dark .log-preview {
  background-color: #1e1e1e;
  color: #e0e0e0;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.theme-dark .log-preview:hover {
  background-color: #2d2d2d;
  border-color: rgba(255, 255, 255, 0.2);
}

/* Melhorar contraste no tema escuro */
.theme-dark .log-preview {
  filter: contrast(1.1);
}

/* Scrollbar personalizada para tema escuro */
.theme-dark .log-preview::-webkit-scrollbar {
  width: 8px;
}

.theme-dark .log-preview::-webkit-scrollbar-track {
  background: #2d2d2d;
  border-radius: 4px;
}

.theme-dark .log-preview::-webkit-scrollbar-thumb {
  background: #4a4a4a;
  border-radius: 4px;
}

.theme-dark .log-preview::-webkit-scrollbar-thumb:hover {
  background: #5a5a5a;
}

/* Scrollbar para tema claro */
.log-preview::-webkit-scrollbar {
  width: 8px;
}

.log-preview::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 4px;
}

.log-preview::-webkit-scrollbar-thumb {
  background: #c0c0c0;
  border-radius: 4px;
}

.log-preview::-webkit-scrollbar-thumb:hover {
  background: #a0a0a0;
}

/* Melhorar aparência do card de log */
.v-card--outlined {
  border-radius: 8px !important;
  overflow: hidden;
}

.theme-dark .v-card--outlined {
  border-color: rgba(255, 255, 255, 0.1) !important;
  background-color: rgba(45, 45, 45, 0.8) !important;
}

/* Título do card de log no tema escuro */
.theme-dark .v-card__title {
  background-color: rgba(30, 30, 30, 0.9) !important;
  color: #ffffff !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
}

/* Botão "Mostrar tudo" no tema escuro */
.theme-dark .v-btn--text {
  color: #64b5f6 !important;
}

.theme-dark .v-btn--text:hover {
  background-color: rgba(100, 181, 246, 0.1) !important;
}

/* Estilos para o dialog moderno */
.modern-dialog .v-dialog {
  border-radius: 20px !important;
  overflow: hidden;
}

.modern-dialog-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px !important;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25) !important;
}

.modern-dialog-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding: 20px 24px !important;
  font-weight: 600 !important;
}

.close-btn {
  transition: all 0.3s ease;
  border-radius: 50% !important;
}

.close-btn:hover {
  background-color: rgba(244, 67, 54, 0.1) !important;
  transform: scale(1.1);
}

/* Tema escuro para dialog moderno */
.theme-dark .modern-dialog-card {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(45, 45, 45, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.theme-dark .modern-dialog-title {
  background: linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: #ffffff !important;
}

.theme-dark .close-btn:hover {
  background-color: rgba(244, 67, 54, 0.2) !important;
}

/* Melhorar aparência dos botões de ação */
.v-card__actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding: 16px 24px !important;
}

.theme-dark .v-card__actions {
  background: linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

/* Melhorar chips e elementos informativos */
.v-chip {
  border-radius: 20px !important;
  transition: all 0.3s ease;
}

.v-chip:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.theme-dark .v-chip {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

/* Melhorar alertas */
.v-alert {
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

.theme-dark .v-alert {
  background-color: rgba(45, 45, 45, 0.9) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

/* Destaques para logs - timestamps, ERROR e DEBUG */
.timestamp-highlight {
  color: #4CAF50 !important;
  background-color: rgba(76, 175, 80, 0.15) !important;
  border-color: rgba(76, 175, 80, 0.4) !important;
  font-weight: 600 !important;
  font-family: Consolas, monospace !important;
  padding: 2px 4px !important;
  border-radius: 3px !important;
  border: 1px solid rgba(76, 175, 80, 0.4) !important;
  transition: all 0.2s ease !important;
  display: inline-block !important;
  text-shadow: none !important;
}

.timestamp-highlight:hover {
  background-color: rgba(76, 175, 80, 0.25) !important;
  border-color: rgba(76, 175, 80, 0.6) !important;
  transform: scale(1.02) !important;
}

.error-highlight {
  color: #F44336 !important;
  background-color: rgba(244, 67, 54, 0.15) !important;
  border-color: rgba(244, 67, 54, 0.4) !important;
  font-weight: 600 !important;
  padding: 2px 4px !important;
  border-radius: 3px !important;
  border: 1px solid rgba(244, 67, 54, 0.4) !important;
  transition: all 0.2s ease !important;
  display: inline-block !important;
  text-shadow: none !important;
}

.error-highlight:hover {
  background-color: rgba(244, 67, 54, 0.25) !important;
  border-color: rgba(244, 67, 54, 0.6) !important;
  transform: scale(1.02) !important;
}

.debug-highlight {
  color: #2196F3 !important;
  background-color: rgba(33, 150, 243, 0.15) !important;
  border-color: rgba(33, 150, 243, 0.4) !important;
  font-weight: 600 !important;
  font-family: Consolas, monospace !important;
  padding: 2px 4px !important;
  border-radius: 3px !important;
  border: 1px solid rgba(33, 150, 243, 0.4) !important;
  transition: all 0.2s ease !important;
  display: inline-block !important;
  text-shadow: none !important;
}

.debug-highlight:hover {
  background-color: rgba(33, 150, 243, 0.25) !important;
  border-color: rgba(33, 150, 243, 0.6) !important;
  transform: scale(1.02) !important;
}
</style> 